<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="writing-assistant-page">
    <!-- SEO-Optimized Hero Section -->
    <header class="hero">
      <div class="container">
        <h1 class="hero-title">{{ $t('seo.writingAssistant.title') }}</h1>
        <p class="hero-subtitle">{{ $t('seo.writingAssistant.description') }}</p>
        <div class="hero-actions">
          <router-link to="/editor" class="btn btn-primary btn-large">
            {{ $t('writingAssistant.tryFree') }}
          </router-link>
          <router-link to="/pricing" class="btn btn-secondary">
            {{ $t('writingAssistant.viewPricing') }}
          </router-link>
        </div>
        <div class="trust-indicators">
          <span class="trust-item">✓ {{ $t('writingAssistant.trust.aiPowered') }}</span>
          <span class="trust-item">✓ {{ $t('writingAssistant.trust.instantResults') }}</span>
          <span class="trust-item">✓ {{ $t('writingAssistant.trust.multilingual') }}</span>
        </div>
      </div>
    </header>

    <!-- Features Showcase Section -->
    <section class="features-showcase">
      <div class="container">
        <h2>{{ $t('writingAssistant.features.title') }}</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">✨</div>
            <h3>{{ $t('writingAssistant.features.improve.title') }}</h3>
            <p>{{ $t('writingAssistant.features.improve.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🔄</div>
            <h3>{{ $t('writingAssistant.features.rewrite.title') }}</h3>
            <p>{{ $t('writingAssistant.features.rewrite.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🌍</div>
            <h3>{{ $t('writingAssistant.features.translate.title') }}</h3>
            <p>{{ $t('writingAssistant.features.translate.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">📝</div>
            <h3>{{ $t('writingAssistant.features.summarize.title') }}</h3>
            <p>{{ $t('writingAssistant.features.summarize.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🎯</div>
            <h3>{{ $t('writingAssistant.features.tone.title') }}</h3>
            <p>{{ $t('writingAssistant.features.tone.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">⚡</div>
            <h3>{{ $t('writingAssistant.features.generate.title') }}</h3>
            <p>{{ $t('writingAssistant.features.generate.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison Section -->
    <section class="comparison">
      <div class="container">
        <h2>{{ $t('writingAssistant.comparison.title') }}</h2>
        <div class="comparison-table">
          <div class="comparison-header">
            <div class="feature-col">{{ $t('writingAssistant.comparison.feature') }}</div>
            <div class="noorwriter-col">n.writer</div>
            <div class="competitor-col">{{ $t('writingAssistant.comparison.competitors') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">
              {{ $t('writingAssistant.comparison.features.aiPowered') }}
            </div>
            <div class="noorwriter-col">✅ {{ $t('writingAssistant.comparison.advanced') }}</div>
            <div class="competitor-col">❌ {{ $t('writingAssistant.comparison.basic') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">
              {{ $t('writingAssistant.comparison.features.freeFeatures') }}
            </div>
            <div class="noorwriter-col">✅ {{ $t('writingAssistant.comparison.generous') }}</div>
            <div class="competitor-col">❌ {{ $t('writingAssistant.comparison.limited') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">
              {{ $t('writingAssistant.comparison.features.languages') }}
            </div>
            <div class="noorwriter-col">✅ {{ $t('writingAssistant.trust.multilingual') }}</div>
            <div class="competitor-col">
              ❌ {{ $t('writingAssistant.comparison.fewLanguages') }}
            </div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('writingAssistant.comparison.features.pricing') }}</div>
            <div class="noorwriter-col">✅ {{ $t('writingAssistant.comparison.affordable') }}</div>
            <div class="competitor-col">❌ {{ $t('writingAssistant.comparison.expensive') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="use-cases">
      <div class="container">
        <h2>{{ $t('writingAssistant.useCases.title') }}</h2>
        <div class="use-cases-grid">
          <div class="use-case-card">
            <h3>{{ $t('writingAssistant.useCases.students.title') }}</h3>
            <p>{{ $t('writingAssistant.useCases.students.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('writingAssistant.useCases.professionals.title') }}</h3>
            <p>{{ $t('writingAssistant.useCases.professionals.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('writingAssistant.useCases.content.title') }}</h3>
            <p>{{ $t('writingAssistant.useCases.content.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('writingAssistant.useCases.business.title') }}</h3>
            <p>{{ $t('writingAssistant.useCases.business.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section for Voice Search -->
    <section class="faq">
      <div class="container">
        <h2>{{ $t('writingAssistant.faq.title') }}</h2>
        <div class="faq-list">
          <div class="faq-item">
            <h3>{{ $t('writingAssistant.faq.q1.question') }}</h3>
            <p>{{ $t('writingAssistant.faq.q1.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('writingAssistant.faq.q2.question') }}</h3>
            <p>{{ $t('writingAssistant.faq.q2.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('writingAssistant.faq.q3.question') }}</h3>
            <p>{{ $t('writingAssistant.faq.q3.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('writingAssistant.faq.q4.question') }}</h3>
            <p>{{ $t('writingAssistant.faq.q4.answer') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <h2>{{ $t('writingAssistant.cta.title') }}</h2>
        <p>{{ $t('writingAssistant.cta.description') }}</p>
        <router-link to="/editor" class="btn btn-primary btn-large">
          {{ $t('writingAssistant.cta.button') }}
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// SEO optimization for Writing Assistant page
useHead({
  title: () => t('seo.writingAssistant.title'),
  meta: [
    {
      name: 'description',
      content: () => t('seo.writingAssistant.description'),
    },
    {
      name: 'keywords',
      content: () => t('seo.writingAssistant.keywords'),
    },
    {
      property: 'og:title',
      content: () => t('seo.writingAssistant.title'),
    },
    {
      property: 'og:description',
      content: () => t('seo.writingAssistant.description'),
    },
    {
      property: 'og:url',
      content: 'https://n-writer.app/ai-writing-assistant',
    },
    {
      name: 'twitter:title',
      content: () => t('seo.writingAssistant.title'),
    },
    {
      name: 'twitter:description',
      content: () => t('seo.writingAssistant.description'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://n-writer.app/ai-writing-assistant',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: t('seo.writingAssistant.title'),
        description: t('seo.writingAssistant.description'),
        url: 'https://n-writer.app/ai-writing-assistant',
        mainEntity: {
          '@type': 'SoftwareApplication',
          name: 'n.writer AI Writing Assistant',
          applicationCategory: 'WritingApplication',
          operatingSystem: 'Web Browser',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
          featureList: [
            'Text Improvement',
            'Content Rewriting',
            'Translation',
            'Summarization',
            'Tone Adjustment',
            'Content Generation',
          ],
        },
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t('writingAssistant.faq.q1.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('writingAssistant.faq.q1.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('writingAssistant.faq.q2.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('writingAssistant.faq.q2.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('writingAssistant.faq.q3.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('writingAssistant.faq.q3.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('writingAssistant.faq.q4.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('writingAssistant.faq.q4.answer'),
            },
          },
        ],
      }),
    },
    // HowTo Schema for Writing Assistant
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: t('writingAssistant.howItWorks.title'),
        description: t('geo.definitions.writingAssistant'),
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: t('writingAssistant.howItWorks.step1.title'),
            text: t('writingAssistant.howItWorks.step1.description'),
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: t('writingAssistant.howItWorks.step2.title'),
            text: t('writingAssistant.howItWorks.step2.description'),
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: t('writingAssistant.howItWorks.step3.title'),
            text: t('writingAssistant.howItWorks.step3.description'),
          },
        ],
      }),
    },
  ],
})
</script>

<style lang="scss" scoped>
.writing-assistant-page {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.hero {
  background: linear-gradient(135deg, var(--nw-brand-slate) 0%, #0f172a 100%);
  color: white;
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  // Animated background orbs
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.3;
    animation: float 20s ease-in-out infinite;
  }

  &::before {
    width: 400px;
    height: 400px;
    background: var(--nw-primary);
    top: -100px;
    right: -100px;
  }

  &::after {
    width: 300px;
    height: 300px;
    background: var(--nw-primary-light);
    bottom: -50px;
    left: -50px;
    animation-delay: -10s;
  }

  .hero-title {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    line-height: 1.1;
    position: relative;
    z-index: 1;
  }

  .hero-subtitle {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
  }

  .hero-actions {
    margin-bottom: 2rem;
    position: relative;
    z-index: 1;

    .btn {
      margin: 0 0.5rem;
    }
  }

  .trust-indicators {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;

    .trust-item {
      font-size: 0.95rem;
      opacity: 0.9;
      font-weight: 500;
    }
  }
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -30px) scale(1.05); }
  50% { transform: translate(-20px, 20px) scale(0.95); }
  75% { transform: translate(20px, 10px) scale(1.02); }
}

.btn {
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  border: none;
  cursor: pointer;

  &-primary {
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: white;
    box-shadow: 0 4px 15px var(--nw-primary-40);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px var(--nw-primary-40);
    }
  }

  &-secondary {
    background: rgba(255, 255, 255, 0.95);
    color: var(--nw-brand-slate);

    &:hover {
      background: white;
      transform: translateY(-2px);
    }
  }

  &-large {
    padding: 1.25rem 2.5rem;
    font-size: 1.1rem;
  }
}

.features-showcase,
.comparison,
.use-cases,
.faq,
.cta {
  padding: 5rem 0;

  h2 {
    text-align: center;
    font-size: 2.5rem;
    font-weight: 800;
    color: var(--nw-brand-slate);
    margin-bottom: 3rem;
  }
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.feature-card {
  padding: 2.5rem 2rem;
  background: white;
  border: 1px solid var(--nw-border-light);
  border-radius: 1.5rem;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px var(--nw-primary-20);
    border-color: var(--nw-primary-20);
  }

  .feature-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    display: inline-block;
    padding: 1rem;
    background: linear-gradient(135deg, var(--nw-primary-10) 0%, rgba(30, 64, 175, 0.05) 100%);
    border-radius: 1rem;
  }

  h3 {
    margin-bottom: 1rem;
    color: var(--nw-brand-slate);
    font-weight: 700;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.comparison-table {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 0;
  margin-top: 2rem;
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--nw-border-light);

  .comparison-header {
    display: contents;
    font-weight: 700;
    
    > div {
      background: var(--nw-bg-secondary);
      padding: 1.25rem 1.5rem;
    }
  }

  .comparison-row {
    display: contents;

    > div {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid var(--nw-border-light);
    }

    &:last-child > div {
      border-bottom: none;
    }
  }
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.use-case-card {
  padding: 2.5rem 2rem;
  background: linear-gradient(135deg, var(--nw-bg-secondary) 0%, rgba(30, 64, 175, 0.03) 100%);
  border-radius: 1.5rem;
  border: 1px solid var(--nw-border-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
    border-color: var(--nw-primary-20);
  }

  h3 {
    margin-bottom: 1rem;
    color: var(--nw-brand-slate);
    font-weight: 700;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.faq-list {
  max-width: 800px;
  margin: 2rem auto 0;
}

.faq-item {
  margin-bottom: 1.5rem;
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--nw-border-light);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  }

  h3 {
    margin-bottom: 0.75rem;
    color: var(--nw-brand-slate);
    font-weight: 700;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.cta {
  background: linear-gradient(135deg, var(--nw-brand-slate) 0%, #0f172a 100%);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: var(--nw-primary);
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.2;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
  }

  h2 {
    color: white;
    position: relative;
    z-index: 1;
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.1rem;
    margin-bottom: 2rem;
    position: relative;
    z-index: 1;
  }

  .btn {
    position: relative;
    z-index: 1;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .comparison-table {
    grid-template-columns: 1fr;

    .comparison-header {
      display: none;
    }
  }

  .trust-indicators {
    flex-direction: column;
    gap: 1rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }
}
</style>
